import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../AuthContext';
import { getUserOrders, getUserDocuments, uploadDocument } from '../api';
import { 
  ClipboardList, 
  AlertCircle, 
  Clock, 
  CheckCircle2, 
  Upload, 
  FileText,
  ChevronRight,
  User,
  Users,
  Building2,
  CalendarDays,
  Plus,
  ArrowRight,
  Circle,
  File,
  Check,
  X,
  MessageSquare
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import StickyHeader from '../components/StickyHeader';
import Footer from '../components/Footer';
import {
  ordersToTasks,
  getTaskStats,
  sortTasksByPriority,
  getUpcomingDeadlines,
  TASK_STATUS,
  BLOCKER_TYPES,
  STATUS_CONFIG
} from '../utils/taskUtils';

// Status pipeline steps for visual display
const PIPELINE_STEPS = [
  { key: TASK_STATUS.INTAKE, label: 'Intake' },
  { key: TASK_STATUS.REVIEW, label: 'Review' },
  { key: TASK_STATUS.FILING, label: 'Filing' },
  { key: TASK_STATUS.WAITING, label: 'Waiting' },
  { key: TASK_STATUS.DONE, label: 'Done' }
];

// Blocker icon component
const BlockerIcon = ({ type }) => {
  switch (type) {
    case BLOCKER_TYPES.USER:
      return <User className="h-3.5 w-3.5" />;
    case BLOCKER_TYPES.EXPERT:
      return <Users className="h-3.5 w-3.5" />;
    case BLOCKER_TYPES.AUTHORITY:
      return <Building2 className="h-3.5 w-3.5" />;
    default:
      return null;
  }
};

// Task Status Pipeline Component
const StatusPipeline = ({ currentStatus }) => {
  const currentIndex = PIPELINE_STEPS.findIndex(s => s.key === currentStatus);
  
  return (
    <div className="flex items-center gap-1" data-testid="status-pipeline">
      {PIPELINE_STEPS.map((step, idx) => {
        const isCompleted = idx < currentIndex;
        const isCurrent = idx === currentIndex;
        const isPending = idx > currentIndex;
        
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center">
              <div 
                className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                  isCompleted ? 'bg-emerald-500 text-white' :
                  isCurrent ? 'bg-blue-500 text-white' :
                  'bg-slate-200 text-slate-500'
                }`}
              >
                {isCompleted ? <Check className="h-3 w-3" /> : idx + 1}
              </div>
              <span className={`text-[10px] mt-1 ${isCurrent ? 'text-blue-600 font-medium' : 'text-slate-400'}`}>
                {step.label}
              </span>
            </div>
            {idx < PIPELINE_STEPS.length - 1 && (
              <div className={`h-0.5 w-4 -mt-4 ${isCompleted ? 'bg-emerald-500' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Document Checklist Component
const DocumentChecklist = ({ checklist, onUpload }) => {
  const requiredDocs = checklist.filter(d => d.required);
  const uploadedCount = requiredDocs.filter(d => d.uploaded).length;
  
  return (
    <div className="space-y-2" data-testid="document-checklist">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600">Documents</span>
        <span className="text-slate-500">{uploadedCount}/{requiredDocs.length} required</span>
      </div>
      <div className="space-y-1.5">
        {checklist.map((doc, idx) => (
          <div 
            key={idx}
            className={`flex items-center justify-between py-1.5 px-2 rounded text-sm ${
              doc.uploaded ? 'bg-emerald-50' : 'bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-2">
              {doc.uploaded ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              ) : (
                <Circle className="h-3.5 w-3.5 text-slate-400" />
              )}
              <span className={doc.uploaded ? 'text-emerald-700' : 'text-slate-600'}>
                {doc.name}{doc.required && !doc.uploaded && <span className="text-red-500 ml-0.5">*</span>}
              </span>
            </div>
            {!doc.uploaded && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 px-2 text-xs"
                onClick={() => onUpload(doc.name)}
              >
                <Upload className="h-3 w-3 mr-1" />
                Upload
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Task Card Component
const TaskCard = ({ task, onUpload, onViewDetails }) => {
  const [expanded, setExpanded] = useState(true); // Default expanded to show documents
  const pendingDocs = task.documentChecklist.filter(d => d.required && !d.uploaded);
  
  return (
    <Card 
      className="overflow-hidden transition-all"
      data-testid={`task-card-${task.id}`}
    >
      <CardContent className="p-0">
        {/* Main Content */}
        <div className="p-5">
          {/* Header Row */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-900 mb-1" data-testid={`task-name-${task.id}`}>
                {task.serviceName}
              </h3>
              <p className="text-xs text-slate-500">
                Started {formatDistanceToNow(new Date(task.createdAt), { addSuffix: true })}
              </p>
            </div>
            <Badge 
              className={`${task.statusConfig.color} border`}
              data-testid={`task-status-badge-${task.id}`}
            >
              {task.statusConfig.label}
            </Badge>
          </div>

          {/* Status Pipeline */}
          <div className="mb-4">
            <StatusPipeline currentStatus={task.status} />
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span>Progress</span>
              <span>{task.progress}%</span>
            </div>
            <Progress value={task.progress} className="h-1.5" />
          </div>
        </div>

        {/* Documents Section - Unified Checklist */}
        <div className="border-t border-slate-100">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full px-5 py-3 flex items-center justify-between text-sm text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span>Documents</span>
              {pendingDocs.length > 0 && (
                <span className="text-slate-400 text-xs">
                  ({pendingDocs.length} pending)
                </span>
              )}
            </span>
            <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
          
          {expanded && (
            <div className="px-5 pb-4">
              <DocumentChecklist 
                checklist={task.documentChecklist} 
                onUpload={(docName) => onUpload(task.id, docName)}
              />
              
              {/* Additional Documents Section */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Additional Documents</p>
                    <p className="text-xs text-slate-400">If requested later</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => onUpload(task.id, 'additional')}
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    Upload
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, color, urgent }) => (
  <div className={`p-4 rounded-xl border ${urgent ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
    <div className="flex items-center gap-3">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className={`text-xs ${urgent ? 'text-red-600 font-medium' : 'text-slate-500'}`}>{label}</p>
      </div>
    </div>
  </div>
);

// Main Dashboard Component
const DashboardPage = () => {
  const { user, loading } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ active: 0, pendingActions: 0, upcomingDeadlines: 0, completed: 0 });
  const [deadlines, setDeadlines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const fetchDashboardData = useCallback(async () => {
    if (user) {
      try {
        setIsLoading(true);
        const [ordersRes, documentsRes] = await Promise.all([
          getUserOrders(),
          getUserDocuments()
        ]);
        
        const transformedTasks = ordersToTasks(ordersRes.data || [], documentsRes.data || []);
        const sortedTasks = sortTasksByPriority(transformedTasks);
        
        setTasks(sortedTasks);
        setStats(getTaskStats(sortedTasks));
        setDeadlines(getUpcomingDeadlines(sortedTasks));
      } catch {
        toast.error('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleFileUpload = async (taskId, documentName) => {
    // Create a file input and trigger it
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('order_id', taskId);
      formData.append('file', file);

      try {
        await uploadDocument(formData);
        toast.success('Document uploaded successfully');
        
        // Refresh data
        const [ordersRes, documentsRes] = await Promise.all([
          getUserOrders(),
          getUserDocuments()
        ]);
        
        const transformedTasks = ordersToTasks(ordersRes.data || [], documentsRes.data || []);
        const sortedTasks = sortTasksByPriority(transformedTasks);
        
        setTasks(sortedTasks);
        setStats(getTaskStats(sortedTasks));
        setDeadlines(getUpcomingDeadlines(sortedTasks));
      } catch (error) {
        toast.error('Failed to upload document');
      }
    };
    
    input.click();
  };

  const filteredTasks = activeFilter === 'all' 
    ? tasks 
    : activeFilter === 'action-required'
    ? tasks.filter(t => t.blocker === BLOCKER_TYPES.USER)
    : tasks.filter(t => t.status === activeFilter);

  const activeTasks = tasks.filter(t => 
    t.status !== TASK_STATUS.DONE && 
    t.status !== TASK_STATUS.CANCELLED
  );

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <StickyHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="h-8 w-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <StickyHeader />

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-7xl">
        {/* Welcome Section */}
        <div className="mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1" data-testid="dashboard-title">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-slate-500" data-testid="dashboard-subtitle">
              {stats.pendingActions > 0 
                ? `You have ${stats.pendingActions} action${stats.pendingActions > 1 ? 's' : ''} requiring your attention`
                : activeTasks.length > 0
                ? `${activeTasks.length} active compliance task${activeTasks.length > 1 ? 's' : ''} in progress`
                : 'No active tasks. Start a new compliance request from Quick Actions.'}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 mb-8" data-testid="stats-grid">
          <StatCard 
            icon={ClipboardList}
            label="Active Tasks"
            value={stats.active}
            color="bg-blue-100 text-blue-600"
          />
          <StatCard 
            icon={AlertCircle}
            label="Action Required"
            value={stats.pendingActions}
            color="bg-red-100 text-red-600"
            urgent={stats.pendingActions > 0}
          />
          <StatCard 
            icon={CheckCircle2}
            label="Completed"
            value={stats.completed}
            color="bg-emerald-100 text-emerald-600"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Tasks */}
          <div className="lg:col-span-2 space-y-6">
            {/* Filter Tabs */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="task-filters">
              <Button
                variant={activeFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter('all')}
                className={activeFilter === 'all' ? 'bg-slate-900' : ''}
              >
                All ({tasks.length})
              </Button>
              <Button
                variant={activeFilter === 'action-required' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter('action-required')}
                className={activeFilter === 'action-required' ? 'bg-red-600 hover:bg-red-700' : 'border-red-200 text-red-700 hover:bg-red-50'}
              >
                <AlertCircle className="h-3.5 w-3.5 mr-1" />
                Action Required ({tasks.filter(t => t.blocker === BLOCKER_TYPES.USER).length})
              </Button>
              <Button
                variant={activeFilter === TASK_STATUS.DONE ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter(TASK_STATUS.DONE)}
                className={activeFilter === TASK_STATUS.DONE ? 'bg-emerald-600' : ''}
              >
                Completed ({tasks.filter(t => t.status === TASK_STATUS.DONE).length})
              </Button>
            </div>

            {/* Task Cards */}
            <div className="space-y-4" data-testid="task-list">
              {filteredTasks.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <ClipboardList className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No tasks yet</h3>
                    <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                      Start your compliance journey by describing your situation or browsing our services.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button onClick={() => navigate('/pricing')} data-testid="browse-services-button">
                        Browse Services
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                filteredTasks.map(task => (
                  <TaskCard 
                    key={task.id}
                    task={task}
                    onUpload={handleFileUpload}
                    onViewDetails={(id) => navigate(`/orders/${id}`)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Estimated Completion Date */}
            <Card data-testid="completion-dates-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-slate-500" />
                  Estimated Completion Date
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deadlines.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No active tasks</p>
                ) : (
                  <div className="space-y-3">
                    {deadlines.slice(0, 5).map((deadline, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50"
                        data-testid={`completion-item-${idx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {deadline.serviceName}
                          </p>
                          <p className="text-xs text-slate-500">
                            {format(new Date(deadline.deadline), 'MMM dd, yyyy')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card data-testid="quick-actions-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  onClick={() => navigate('/pricing')}
                  data-testid="quick-action-new"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Start New Compliance
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/contact')}
                  data-testid="quick-action-support"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DashboardPage;
